import { DispatchForecast, Likelihood, Registrations } from '@/app/models/com/registrations.model';
import { DsyListCustomConf, DsyListElementConf, DsyTagConfig } from '@/app/models/dsy-drop-down.model';
import { DispatchForecastService, normalizeDispatch } from '@/app/services/dispatch-forecast.service';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-shared-services';
import { environment } from '@/environments/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DispatchForecastWidget } from '@/app/models/widget.model';
import { TooltipModule } from 'primeng/tooltip';
import { UserService } from '@/app/services/user.service';
import { DatePipe, SlicePipe } from '@angular/common';
import { DispatchForecastDatePipe } from '@/app/pipes/dispatch-forecast-date.pipe';


@Component({
  selector: 'dr-flex-home-ui-dispatch-forecast-widget',
  templateUrl: './dispatch-forecast-widget.component.html',
  styleUrl: './dispatch-forecast-widget.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TranslateModule, RouterModule, WidgetHeaderComponent, TooltipModule, EmptyStateComponent, DatePipe, DispatchForecastDatePipe, SlicePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
  
})
export class DispatchForecastWidgetComponent implements OnInit{
  widget = input.required<DispatchForecastWidget>();
  
   // MARK: Injectable
  private readonly dispatchForecastService = inject(DispatchForecastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cookieService = inject(CookieService);
  private readonly userService = inject(UserService);
  // MARK: Signals
  readonly locale = signal('');
  //readonly timezone = signal('');
  readonly programs = signal<DsyListElementConf[]>([]);
  readonly showDropdown = signal(false)
    private readonly dsyDropDownListConfig: DsyListCustomConf = {
    minWidth: '12rem',
    maxWidth: '16rem',
    maxHeight: '13.5rem',
    status: 'default',
    border: false,
    disabled: false,
    exclusiveSubrowVisibility: true,
    reverseRender: false,
    isNavBarStyle: false,
  };
  
  readonly registrations = signal<Map<string, Registrations>>(new Map());
  readonly forecastData = signal<Registrations[] | null>(null);
  readonly averageLikelihood = signal<Likelihood | null>(null);
  
  readonly selectedForecast = signal<DispatchForecast[]>([]);

  readonly dropDownListConfig = computed<DsyListCustomConf>(() => {
    const porgramsLength = this.programs().length;
    return {
      ...this.dsyDropDownListConfig,
      itemsToDisplay: porgramsLength,
    };
  });
  readonly dropDownTagConfig: DsyTagConfig = {
    variant: 'secondary',
  };
  // OnInit
  ngOnInit(): void {
    this.locale.set(this.cookieService.getI18NLocale() || environment.defaults.locale);
    this.fetchPrograms()
  }
  // MARK: Methods
  private fetchPrograms(): void {
    this.dispatchForecastService
      .getProgramNameBySession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        //.log("GraphQL response:", res);
        const filtered =
          this.dispatchForecastService.filterProgramsByPortfolio(
            res.data.registrations
          );
        this.forecastData.set(filtered)
        const map = new Map<string, Registrations>();
        const program = 
          filtered.map((r) => {
            
            const normalizedForecast = normalizeDispatch(r.dispatch_forecast);

            const pairId = `${r.program.id}_${JSON.stringify(normalizedForecast)}`;

            map.set(pairId,r)
            return{
              id: pairId, 
              labelContent: r.program.display_labels[0].label,
              onListElementClick: () =>
                this.selectProgram(pairId),
            }
          });
          this.registrations.set(map);
          const sortedPrograms = this.sortProgramsByForecast(program);
          sortedPrograms[0].isSelected = true;
          const registration = this.registrations().get(sortedPrograms[0].id);
          if (registration) {
            this.updateAverage(registration);
          }
          this.programs.set(sortedPrograms)
      });
    }
    private selectProgram(pairId: string): void {
      this.programs.set(
          this.programs().map(p => ({
            ...p,
            isSelected: p.id === pairId,
          }))
        );
        const registration = this.registrations().get(pairId); 
        if (registration) {
          this.updateAverage(registration); 
        }
    }

  
  private updateAverage(registration: Registrations): void {
    const avg =this.dispatchForecastService.getMostFrequentLikelihood(registration.dispatch_forecast);
    this.averageLikelihood.set(avg);
    this.selectedForecast.set(registration.dispatch_forecast);
  }

  isToday(value: string): boolean {
    const today = this.formatToday(); // YYYY-MM-DD
    //console.log("Today", today)
    //console.log("Forecast Date", value)
    return value === today;
  }

  isPast(value: string): boolean {
    const today = this.formatToday();
    return value < today;
  }

  private formatToday(): string {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  get auditForecast(){
    return this.selectedForecast().find(f => f.isAuditWindow)
  }
  get auditWindowRange(): { start: string; end: string } | null {
    const auditDays = this.selectedForecast()
      .filter(f => f.isAuditWindow);

    if (auditDays.length === 0) {
      return null;
    }

    return {
      start: auditDays[0].date,
      end: auditDays[auditDays.length - 1].date
    };
  }
  private hasForecastData(pairId: string): boolean {
    const registration = this.registrations().get(pairId);
    return !!registration?.dispatch_forecast?.length;
  }

  private sortProgramsByForecast(programs: DsyListElementConf[]): DsyListElementConf[] {
    return programs.sort((a, b) => {
      const hasDataA = this.hasForecastData(a.id);
      const hasDataB = this.hasForecastData(b.id);

      //Programs with data first
      if (hasDataA !== hasDataB) {
        return hasDataA ? -1 : 1;
      }

      // Then alphabetical
      return a.labelContent.localeCompare(b.labelContent);
    });
  }

}

